import { LitElement, TemplateResult, css, html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import {
    BackendPlan,
    METALLIC_LEVEL,
    PRODUCT_TYPE,
    enrollPlanHandler,
    getPharmacyLink,
    getProviderLink,
    keepPlanHandler,
    getMedicationLink,
    recommendPlanHandler,
    selectNewPlanHandler,
    getPlanRate,
    getPlanName,
} from '../util/plans';
import { makeCustomEvent, EVENT } from '../util/events';
import {
    metalPlanTooltipMap,
    productTypeTooltipMap,
    overallStarRatingTooltip,
    csrTooltip,
    easyPricingTooltip,
} from '../util/tooltips';
import { consume } from '@lit/context';
import { inputResponseContext } from '../contexts/inputResponse';
import { BackendFormResponse, Producer } from '../util/api';
import { formatCurrency } from '../util/format';
import { breakpointContext } from '../contexts/breakpoint';
import { BREAKPOINT } from '../util/breakpoints';
import { classMap } from 'lit/directives/class-map.js';
import { sourceContext } from '../contexts/source';
import { deliveryMethodContext } from '../contexts/deliveryMethod';
import { SOURCE, sourcesToRecommend, isEmbeddedAgentEmailDelivery } from '../util/source';
import { choose } from 'lit/directives/choose.js';
import { PAGE } from '../util/page';
import './tooltip';
import './heatmap';
import './checkbox';
import { filtersContext } from '../contexts/filters';
import { Filters } from '../util/filter-sort';
import { getAnalyticPageDescription, getAnalyticPlanDescription } from '../util/analytics';
import { agentContext } from '../contexts/agent';
import { CURRENT_YEAR, NEXT_YEAR, DISCONTINUOUS_PLAN_DATE } from '../util/config';
import { readOnlyContext } from '../contexts/readOnly';

@customElement('rq-plan-card')
export class MarketplacePlanCard extends LitElement {
    static styles = css`
        .container {
            border-radius: 4px;
            border: 1px solid var(--lh-color-neutral-200, #d1d3d4);
            box-shadow: 0px 8px 16px 0px rgba(0, 76, 108, 0.08);
        }
        .border {
            border: 3px solid var(--dark-primary-dark, #006894);
        }
        .top-banner {
            display: flex;
            flex-direction: row;
            gap: 8px;
        }
        .desktop-wrapper {
            box-sizing: border-box;
            padding: 24px;
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            gap: 26px;
        }
        .mobile-wrapper {
            box-sizing: border-box;
            padding: 24px;
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
        .details {
            display: flex;
            flex-direction: column;
            gap: 24px;
            flex: 3;
        }
        .price {
            flex: 1;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            background: var(--lh-color-primary-50, #ebf9ff);
            gap: 22px;
            padding: 24px;
        }
        .price-shopper {
            gap: 20%;
        }
        .attributes {
            display: flex;
            flex-direction: row;
            gap: 32px;
            align-content: center;
            align-items: center;
            flex-wrap: wrap;
        }
        .mobile-attributes {
            gap: 16px;
        }
        .easy-pricing {
            display: flex;
            flex-direction: row;
            gap: 4px;
        }
        .rating {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 4px;
        }
        .coverage-wrapper {
            display: flex;
            flex-direction: row;
            gap: 12px;
            flex-wrap: wrap;
        }
        .mobile-coverage-wrapper {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            flex-wrap: wrap;
            row-gap: 8px;
        }
        .coverage {
            display: flex;
            flex-direction: row;
            gap: 4px;
        }
        .stars {
            color: var(--lh-color-primary-500, #00aeef);
        }
        .copay-wrapper {
            display: flex;
            flex-direction: column;
            gap: 10px;
        }
        .mobile-copay-wrapper {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
        .copay {
            display: flex;
            flex-direction: column;
            width: 100%;
        }
        .costs {
            display: flex;
            flex-direction: column;
            gap: 16px;
            align-items: center;
        }
        .price-buttons {
            display: flex;
            flex-direction: column;
            gap: 16px;
            align-items: center;
        }
        .price-text {
            white-space: pre-wrap;
        }
        .action-bar {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
        }
        .recommend-bar {
            flex-direction: column;
            gap: 16px;
            align-items: start;
        }
        .hide {
            display: none;
        }
        .icon {
            color: white;
        }
        .heatmap {
            margin: auto;
        }
        .full-width {
            width: 100%;
        }
        .pb-sm {
            padding-bottom: 14px;
        }
        .discontinued-alert {
            display: flex;
            padding: 0.7rem;
            justify-content: center;
            align-items: center;
            gap: 1.5rem;
            border-radius: 0.25rem;
            background: var(--Warning-Warning, #f9e719);
        }
        .align-icon-tooltip {
            display: inline-flex;
            align-items: center;
            gap: 8px;
        }
    `;

    @property()
    plan!: BackendPlan;

    @consume({ context: inputResponseContext, subscribe: true })
    inputResponse!: BackendFormResponse;

    @consume({ context: breakpointContext, subscribe: true })
    @state()
    breakpoint!: BREAKPOINT;

    @consume({ context: sourceContext, subscribe: true })
    source!: SOURCE;

    @consume({ context: deliveryMethodContext, subscribe: true })
    deliveryMethod?: string;

    @property()
    comparePlans: BackendPlan[] = [];

    @consume({ context: readOnlyContext, subscribe: true })
    @state()
    readOnly = false;

    @consume({ context: filtersContext, subscribe: true })
    filters!: Filters;

    @consume({ context: agentContext })
    agent?: Producer;

    @state()
    heatmapWidth!: string;

    constructor() {
        super();
        this.setHeatmapWidth = this.setHeatmapWidth.bind(this);
    }

    connectedCallback(): void {
        super.connectedCallback();
        this.addEventListener(EVENT.CHECKBOX_CLICK, this.compareSelectionHandler as EventListener);
        window.addEventListener('resize', this.setHeatmapWidth);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener(EVENT.CHECKBOX_CLICK, this.compareSelectionHandler as EventListener);
        window.removeEventListener('resize', this.setHeatmapWidth);
    }

    firstUpdated() {
        this.setHeatmapWidth();
    }

    private setHeatmapWidth() {
        const priceBoxWidth = this.priceBox?.clientWidth;
        if (!priceBoxWidth || Number.isNaN(priceBoxWidth)) return;
        // 48 is the combined padding on the left and right
        const width = `${this.priceBox?.clientWidth - 48}px`;
        if (width !== this.heatmapWidth) {
            this.heatmapWidth = width;
        }
    }

    private compareSelectionHandler({ detail: { value } }: CustomEvent) {
        this.dispatchEvent(makeCustomEvent(EVENT.COMPARE_SELECTION, { planCode: value }));
    }

    @query('.price')
    priceBox: HTMLDivElement;

    get topPickChip() {
        // TODO: remove below with top pick including undoing pickOrder deletion
        return nothing;
        // if (this.plan.pickOrder === undefined || this.plan.pickOrder < 0) return nothing;
        // return html`<div>
        //     <lh-chip variant="filled" color="primary">Top Pick</lh-chip>
        // </div>`;
    }

    get easyPricing() {
        if (!this.plan?.pricing || this.plan.pricing.toLowerCase() !== 'easy') return nothing;
        return html`
            <rq-tooltip .description=${easyPricingTooltip}
                ><div class="easy-pricing">
                    <lh-icon name="tag" size="sm"></lh-icon><lh-typography size="sm">Easy Pricing</lh-typography>
                </div>
            </rq-tooltip>
        `;
    }

    get productType() {
        if (!this.plan.productType) return nothing;
        return html`<div>
            <lh-typography size="sm">
                <lh-tooltip .description=${productTypeTooltipMap.get(this.plan.productType as PRODUCT_TYPE)}>
                    ${this.plan.productType.toUpperCase()} Plan
                </lh-tooltip>
            </lh-typography>
        </div>`;
    }

    get metallicLevel() {
        if (!this.plan.metal) return nothing;
        const tooltip = metalPlanTooltipMap.get(this.plan.metal as METALLIC_LEVEL);
        return html`<div>
            <lh-typography size="sm">
                <lh-tooltip .description=${tooltip ?? ''}>
                    ${this.plan.metal.charAt(0).toUpperCase() + this.plan.metal.slice(1)} Level
                </lh-tooltip>
            </lh-typography>
        </div>`;
    }

    get rating() {
        // Were asked to differentiate these plans by adding a "New Plan" label (May 2025)
        const newPlans = ['11512NC0430001', '11512NC0430002', '11512NC0430003'];
        // const effectiveYear = this.inputResponse?.effectiveStart?.split('/')?.pop();
        let isNewHMOPlan = false;
        if (this.plan.productType) {
            isNewHMOPlan = this.plan.productType.includes(PRODUCT_TYPE.HMO);
        }
        if (!this.plan.overallQualityRating && this.plan.overallQualityRating !== 0) return nothing;
        const isNewPlan = newPlans.some((newPlan) => this.plan.bolCode?.includes(newPlan));
        const planRating = Number(this.plan.overallQualityRating);
        return html` <lh-typography size="sm">
            <lh-tooltip description=${overallStarRatingTooltip}>
                <div class="rating">
                    <span>Rating:</span>
                    <span>
                        ${planRating === 0 || isNaN(planRating)
                            ? `${isNewPlan || isNewHMOPlan ? 'New Plan - ' : ''}Not Rated`
                            : Array.from(
                                  { length: 5 },
                                  (_, i) => html`
                                      <span class="stars"
                                          ><lh-icon name="${planRating > i ? 'star-fill' : 'star'}"></lh-icon
                                      ></span>
                                  `
                              )}
                    </span>
                </div>
            </lh-tooltip>
        </lh-typography>`;
    }

    get csr() {
        // Back-end developer said that planVariant of "4", "5", and "6" are CSR plans
        // may want to add that check instead
        if (!this.inputResponse.csrPercent || !getPlanName(this.plan).toLowerCase().includes('csr')) return nothing;
        return html`<div>
            <lh-typography size="sm"
                ><lh-tooltip description=${csrTooltip}>CSR: ${this.inputResponse.csrPercent}</lh-tooltip></lh-typography
            >
        </div>`;
    }

    viewPlanDetails() {
        this.dispatchEvent(
            makeCustomEvent(EVENT.CHANGE_PAGE, {
                page: PAGE.DETAILS,
                planId: this.plan?.planId,
            })
        );
    }

    get originalPlan() {
        return this.plan.planCode === this.inputResponse.currentPlan;
    }

    get isCompared() {
        return this.comparePlans.some((p) => p.planCode === this.plan.planCode);
    }

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    get enrollButton() {
        const handler = () => {
            enrollPlanHandler(this.plan.planId, this.plan.qhpId, this.inputResponse);
        };
        const analyticTag = `rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
            this.filters
        )}:enroll-${getAnalyticPlanDescription(this.plan)}:button-filled`;
        return html`<lh-button
            analytics-id=${analyticTag}
            icon-position="left"
            icon="note-pencil"
            variant="filled"
            @click=${handler}
            full-width
            .disabled=${this.readOnly}
        >
            Enroll in this Plan
        </lh-button>`;
    }

    get recommendButton() {
        const handler = () => recommendPlanHandler(this.plan.planId);
        const analyticTag = `rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
            this.filters
        )}:recommend-${getAnalyticPlanDescription(this.plan)}:button-filled`;
        return html`<lh-button
            icon-position="left"
            analytics-id=${analyticTag}
            icon="seal-check"
            variant="filled"
            @click=${handler}
            full-width
            .disabled=${this.readOnly}
        >
            Recommend Plan
        </lh-button>`;
    }

    get keepPlanButton() {
        const handler = () => keepPlanHandler(this.plan.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
            this.filters
        )}:keep-${getAnalyticPlanDescription(this.plan)}:button-filled`;
        return html`<lh-button variant="filled" analytics-id=${analyticTag} @click=${handler} full-width
            >Keep Original Plan</lh-button
        >`;
    }

    get selectNewButton() {
        const handler = () => selectNewPlanHandler(this.plan.planCode);
        const analyticTag = `rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
            this.filters
        )}:select-new-${getAnalyticPlanDescription(this.plan)}:button-filled`;
        return html`<lh-button variant="filled" analytics-id=${analyticTag} @click=${handler} full-width
            >Select New Plan</lh-button
        >`;
    }

    get actionButton() {
        // Disable action button if plan is not selectable
        if (this.plan.notSelectable) {
            return nothing;
        }
        // When an embedded agent chooses to email the quote to their client, act like SOURCE.EMAIL
        // and always recommend (the embedded-agent quote request isn't flagged as producer context).
        if (isEmbeddedAgentEmailDelivery(this.source, this.deliveryMethod)) {
            return this.recommendButton;
        }
        return choose(
            this.source,
            [
                ...sourcesToRecommend.map((s): [SOURCE, () => TemplateResult] => [
                    s,
                    () => (this.isProducer ? this.recommendButton : this.enrollButton),
                ]),
                [SOURCE.DIRECT_SHOPPER, () => this.enrollButton],
                [SOURCE.PABOL, () => this.enrollButton],
                [SOURCE.EMBEDDED_AGENT, () => this.enrollButton],
                [SOURCE.SALESFORCE, () => this.enrollButton],
                [
                    SOURCE.CHANGE_APP,
                    () => {
                        if (this.originalPlan) {
                            return this.keepPlanButton;
                        }
                        return this.selectNewButton;
                    },
                ],
            ],
            () => this.enrollButton
        );
    }

    get topBanner() {
        let label = '';
        if (this.plan.producerRecommended || this.originalPlan) {
            if (this.plan.producerRecommended) {
                label = this.isShopper ? 'Your agent recommended this plan.' : 'Recommended plan';
            } else {
                label = 'You originally applied for this plan.';
            }
        } else if (this.plan.agentRecommendedPlan) {
            label = 'Agent Recommended Plan';
        } else if (this.plan.planPopularityIndex > 0 && this.plan.bcbsncRecommendedPlan) {
            label = 'Popular and Lower Cost Plan';
        } else if (this.plan.planPopularityIndex < 0 && this.plan.bcbsncRecommendedPlan) {
            label = 'Lower Cost Plan';
        } else if (this.plan.planPopularityIndex > 0) {
            label = 'Popular Plan';
        } else {
            return nothing;
        }
        return html`<lh-container
            theme="primary-dark"
            spacing="none"
            content-width="fluid"
            padding-top="sm"
            padding-bottom="sm"
            gutters="sm"
            ><div class="top-banner">
                <span class="icon">
                    <lh-icon name="seal-check"></lh-icon>
                </span>
                <lh-typography>${label}</lh-typography>
            </div></lh-container
        >`;
    }

    get stackCompareActionMobile() {
        return (
            (sourcesToRecommend.includes(this.source) ||
                isEmbeddedAgentEmailDelivery(this.source, this.deliveryMethod)) &&
            this.breakpoint === BREAKPOINT.PHONE
        );
    }

    private getCopayFromSeparate(inn: string, oon?: string) {
        const separator = this.breakpoint === BREAKPOINT.PHONE ? '\n' : ' | ';
        return `In-network: ${inn}${separator}Out-of-network: ${oon && oon.toLowerCase() !== 'n/a' ? oon : 'Not Covered'}`;
    }

    private getCopayFromCombined(copay: string, concatCopay = true): string {
        let [inn, oon] = copay.split('/');
        inn = inn.toLowerCase().includes('no charge') ? '$0' : inn?.split(' ').filter((p) => p.includes('$'))[0];
        oon = oon?.split(' ').filter((p) => p.includes('$'))[0];

        if (concatCopay) {
            inn = inn?.concat(' copayment');
            if (oon) {
                oon = oon.concat(' copayment');
            }
        }
        return this.getCopayFromSeparate(inn, oon);
    }

    get isShopper() {
        return this.inputResponse?.context?.toLowerCase() === 'shopper';
    }

    get isProducer() {
        return Boolean(this.agent || this.inputResponse?.context?.toLowerCase() === 'producer');
    }

    get monthlyResponsibilityRate() {
        return getPlanRate(this.plan);
    }

    get costBreakdown() {
        const primary = this.inputResponse.applicants.find((a) => a.type === 'Primary');
        const spouse = this.inputResponse.applicants.find((a) => a.type === 'Spouse');
        const children = this.inputResponse.applicants.filter((a) => a.type === 'Child');
        let primaryRate: number | undefined;
        let spouseRate: number | undefined;
        let childrenRates: number[] = [];
        if ((this.plan?.rates || []).length) {
            primaryRate = this.plan?.rates.find((r) => r.applicantIndex === primary?.index)?.applicantRate;
            spouseRate = this.plan?.rates.find((r) => r.applicantIndex === spouse?.index)?.applicantRate;
            childrenRates = this.plan?.rates
                .filter((r) => children.some((c) => c.index === r.applicantIndex))
                .map((c) => c.applicantRate);
        } else if ((this.plan?.applicantRate || []).length) {
            primaryRate = this.plan?.applicantRate?.find(
                (r) => r.personId.toLowerCase() === 'subscriber'
            )?.monthlyPremium;
            childrenRates =
                this.plan?.applicantRate
                    ?.filter((r) => r.personId.toLowerCase() === 'dependent')
                    .map((d) => d.monthlyPremium) || [];
        }
        const subsidy =
            this.plan.marketplace === 'onExchange' && this.inputResponse?.subsidy?.subsidyAmt
                ? this.inputResponse?.subsidy?.subsidyAmt
                : undefined;

        // adding in-line styles because any classes sent as a description would have to be handled in tooltip css
        const lineItemStyle = 'display: grid; grid-template-columns: 20px 60px auto; align-items: center; gap: 8px;';
        const wrapperStyle = `color: var(--lh-color-neutral-white); display: flex; flex-direction: column; gap: 4px; width: 100%; white-space: nowrap;`;

        return `<lh-typography type="text" size="xxs"><div style="${wrapperStyle}">
            <div style="${lineItemStyle}"><div>+</div><div>${formatCurrency(primaryRate)}</div><div>You${!this.isShopper ? 'r client' : ''}</div></div>
            ${spouseRate ? `<div style="${lineItemStyle}"><div>+</div><div>${formatCurrency(spouseRate)}</div><div> Your ${!this.isShopper ? "client's " : ''}spouse/domestic partner</div></div>` : ''}
            ${childrenRates.length ? childrenRates.map((r) => `<div style="${lineItemStyle}"><div>+</div><div>${formatCurrency(r)}</div><div> Your ${!this.isShopper ? "client's " : ''}child</div></div>`).join('') : ''}
            ${subsidy && Number(subsidy) > 0 ? `<div style="${lineItemStyle}"><div>-</div><div>${formatCurrency(subsidy)}</div><div>Your ${!this.isShopper ? "client's " : ''}estimated subsidy</div></div>` : ''}
            <div style="border-bottom: 1px solid var(--lh-color-neutral-white);"></div>
            <div style="${lineItemStyle}"><div></div><div>${formatCurrency(this.monthlyResponsibilityRate)}</div><div> Total</div></div></div>
            <br/>
        <div style="color: var(--lh-color-neutral-white);">Note: There is no charge for children beyond the three oldest under age 21.</div></lh-typography>`;
    }

    private getPriceBox() {
        return html`<div class=${classMap({ price: true, 'price-shopper': this.isShopper && !this.isMobile })}>
            ${this.isShopper
                ? nothing
                : html`<div class="heatmap">
                      <rq-heatmap .plan=${this.plan} width=${this.heatmapWidth}></rq-heatmap>
                  </div>`}
            <div class="costs">
                <lh-typography size="lg" alignment="center">Cost per month</lh-typography>
                <div class=${classMap({ 'full-width': this.isMobile })}>
                    <rq-tooltip .description=${this.costBreakdown} position=${this.isMobile ? 'top' : 'left'}
                        ><lh-typography type="heading" selector="h2" size="xxl" alignment="center"
                            >${formatCurrency(this.monthlyResponsibilityRate)}</lh-typography
                        >
                    </rq-tooltip>
                </div>
                ${this.plan.totalRate && this.monthlyResponsibilityRate !== this.plan.totalRate
                    ? html` <lh-typography size="md" alignment="center">was $${this.plan.totalRate}</lh-typography> `
                    : html`<lh-typography size="md" alignment="center">&nbsp;</lh-typography>`}
            </div>
            <div class="price-buttons">
                <lh-button
                    icon-position="left"
                    icon="scroll"
                    variant="outlined"
                    analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
                        this.filters
                    )}:details-${getAnalyticPlanDescription(this.plan)}:button-outlined"
                    @click=${this.viewPlanDetails}
                    full-width
                    >View Plan Details</lh-button
                >
                ${this.actionButton}
            </div>
        </div>`;
    }

    render() {
        if (!this.plan) return nothing;
        const isDiscontinued = this.plan.discontinued === true || this.plan.discontinued === 'true';
        return html`<div
            class=${classMap({ container: true, border: Boolean(this.plan.producerRecommended || this.originalPlan) })}
        >
            ${this.topBanner}
            <div
                class=${classMap({
                    'desktop-wrapper': !this.isMobile,
                    'mobile-wrapper': this.isMobile,
                })}
                slot="content"
            >
                <div class="details">
                    ${this.shouldAlertDiscontinuation(isDiscontinued)
                        ? html`<lh-container
                              margin-top="none"
                              margin-bottom="none"
                              padding-top="none"
                              padding-bottom="none"
                              context-id="custom-section"
                              content-width="fluid"
                              spacing="none"
                              gutters="none"
                              ><lh-typography size="sm" alignment="left" class="discontinued-alert">
                                  <span class="align-icon-tooltip"
                                      ><lh-icon name="info" size="md"></lh-icon>&nbsp;&nbsp;
                                      <lh-tooltip
                                          description="If you select this plan for ${CURRENT_YEAR}, you will be migrated to a closely matched plan in ${NEXT_YEAR}."
                                          >This plan will
                                          <strong>no longer be available in ${NEXT_YEAR}.</strong>
                                      </lh-tooltip></span
                                  >
                              </lh-typography></lh-container
                          >`
                        : nothing}
                    ${this.topPickChip}
                    <div>
                        <lh-typography size="lg">
                            <strong>${this.plan.marketingName}</strong>
                        </lh-typography>
                    </div>
                    <div class=${classMap({ attributes: true, 'mobile-attributes': this.isMobile })}>
                        ${this.easyPricing} ${this.productType} ${this.metallicLevel} ${this.rating} ${this.csr}
                    </div>
                    ${this.isMobile ? this.getPriceBox() : nothing}
                    <div>
                        <div class="pb-sm">
                            <lh-typography type="label" size="xxs">Check coverage in this plan</lh-typography>
                        </div>
                        <div
                            class=${classMap({
                                'coverage-wrapper': !this.isMobile,
                                'mobile-coverage-wrapper': this.isMobile,
                            })}
                        >
                            <div class="coverage">
                                <lh-button
                                    analytics-id="rate-quote:${this.source}:u65:plans-${getAnalyticPageDescription(
                                        this.filters
                                    )}:medications-${getAnalyticPlanDescription(this.plan)}:button-text"
                                    icon="arrow-square-out"
                                    icon-position="right"
                                    target="_blank"
                                    variant="text"
                                    size="md"
                                    href=${getMedicationLink(this.plan.pricing, this.inputResponse?.effectiveStart)}
                                >
                                    Medication<span class="space"></span>
                                </lh-button>
                            </div>
                            ${getProviderLink(this.plan.lobCode)
                                ? html`<div class="coverage">
                                      <lh-button
                                          analytics-id="rate-quote:${this
                                              .source}:u65:plans-${getAnalyticPageDescription(
                                              this.filters
                                          )}:doctors-${getAnalyticPlanDescription(this.plan)}:button-text"
                                          icon="arrow-square-out"
                                          icon-position="right"
                                          target="_blank"
                                          variant="text"
                                          size="md"
                                          href=${getProviderLink(this.plan.lobCode)}
                                      >
                                          Doctor<span class="space"></span>
                                      </lh-button>
                                  </div>`
                                : nothing}
                            ${getPharmacyLink(this.plan.heatmapNetwork, this.inputResponse?.effectiveStart)
                                ? html`<div class="coverage">
                                      <lh-button
                                          analytics-id="rate-quote:${this
                                              .source}:u65:plans-${getAnalyticPageDescription(
                                              this.filters
                                          )}:pharmacies-${getAnalyticPlanDescription(this.plan)}:button-text"
                                          icon="arrow-square-out"
                                          icon-position="right"
                                          variant="text"
                                          target="_blank"
                                          size="md"
                                          href=${getPharmacyLink(
                                              this.plan.heatmapNetwork,
                                              this.inputResponse?.effectiveStart
                                          )}
                                      >
                                          Pharmacy<span class="space"></span>
                                      </lh-button>
                                  </div>`
                                : nothing}
                        </div>
                    </div>
                    <div
                        class=${classMap({
                            'copay-wrapper': !this.isMobile,
                            'mobile-copay-wrapper': this.isMobile,
                        })}
                    >
                        <div class="copay">
                            <lh-typography size="md" alignment="left"
                                ><strong class="price-text">Urgent care visits</strong></lh-typography
                            >
                            <lh-typography size="md" alignment="left"
                                ><span class="price-text"
                                    >${this.plan.urgentCareInn
                                        ? this.getCopayFromSeparate(this.plan.urgentCareInn, this.plan.urgentCareOon)
                                        : this.getCopayFromCombined(this.plan.urgentCare)}</span
                                ></lh-typography
                            >
                        </div>
                        <div class="copay">
                            <lh-typography size="md" alignment="left"
                                ><strong class="price-text">Primary doctor visit</strong></lh-typography
                            >
                            <lh-typography size="md" alignment="left"
                                ><span class="price-text"
                                    >${this.plan.primaryCareInn
                                        ? this.getCopayFromSeparate(this.plan.primaryCareInn, this.plan.primaryCareOon)
                                        : this.getCopayFromCombined(this.plan.primaryCare)}</span
                                ></lh-typography
                            >
                        </div>
                        <div class="copay">
                            <lh-typography size="md" alignment="left"
                                ><strong class="price-text">Tier 1 medications</strong></lh-typography
                            >
                            <lh-typography size="md" alignment="left"
                                ><span class="price-text"
                                    >${this.plan.tier1MedInn
                                        ? this.getCopayFromSeparate(this.plan.tier1MedInn, this.plan.tier1MedOnn)
                                        : this.getCopayFromCombined(this.plan.rxGeneric)}</span
                                ></lh-typography
                            >
                        </div>
                        <div class="copay">
                            <lh-typography size="md" alignment="left"
                                ><strong class="price-text">Deductible</strong></lh-typography
                            >
                            <lh-typography size="md" alignment="left">
                                <span class="price-text"
                                    >${this.plan.individualDeductibleInn
                                        ? this.getCopayFromSeparate(
                                              this.plan.individualDeductibleInn ||
                                                  `$${Number(this.plan.deductible).toLocaleString()}`,
                                              this.plan.individualDeductibleOon
                                          )
                                        : this.getCopayFromCombined(this.plan.individualDeductible, false)}</span
                                ></lh-typography
                            >
                        </div>
                    </div>
                    <div
                        class=${classMap({
                            'action-bar': true,
                            'recommend-bar': this.stackCompareActionMobile,
                        })}
                    >
                        <div>
                            <rq-checkbox
                                name="compare"
                                tabindex="0"
                                value=${this.plan.planCode}
                                .checked=${this.isCompared || nothing}
                                .disabled=${this.comparePlans.length >= 3 && !this.isCompared}
                                >Compare</rq-checkbox
                            >
                        </div>
                    </div>
                </div>
                ${!this.isMobile ? this.getPriceBox() : nothing}
            </div>
        </div>`;
    }

    private shouldAlertDiscontinuation(isDiscontinued: boolean) {
        return (
            isDiscontinued &&
            new Date(DISCONTINUOUS_PLAN_DATE) >=
                new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-plan-card': MarketplacePlanCard;
    }
}
